import React, { KeyboardEvent, useEffect, useRef, useState } from 'react'
import {
  decodeNewLineText,
  getApplicableRange,
  isAbnormal,
  isOutsideNormalRange,
} from '../utils'
import { LabResult, LmsObservation, LmsValues } from '../models'
import { Patient } from 'fhir/r4'
import { fullDateTimeFormat } from '../../../utils/dateUtils'
import ObservationList from './observations/ObservationList'
import { useAppSelector } from '../../../app/hooks'
import { selectedLmsActiveTab } from '../lmsSlice'
import {
  ABNORMAL,
  Enter,
  HIDE_NOTES,
  LmsTestTab,
  NO_VALID_RANGE,
  NUMERIC,
  NUMERIC_OUTCOME,
  observationText,
  orderStatusRejected,
  OUT_SIDE_NORMAL_RANGE,
  REJECTED_TEST,
  SHORT_TEXT,
  SHOW_NOTES,
} from '../constants'
import { LMS_RESULT_EDIT } from '../../../utils/roles'

type Props = {
  activeId: number
  test: LabResult
  patient: Patient | null
  onValueChange: (val: string, index: number) => void
  onNotesChange: (val: string, index: number) => void
  onExtraValueChange: (val: string, index: number) => void
  onValuesChange: (values: LmsValues, index: number) => void
  onEnter: (id: number) => void
  disabled: boolean
  isReferred: boolean
  isEdit: boolean
  parameterIds?: number[]
  keycloakService?: any
}

const TestRow = ({
  activeId,
  test,
  patient,
  onValueChange,
  onNotesChange,
  onExtraValueChange,
  onValuesChange,
  onEnter,
  disabled,
  isReferred,
  isEdit,
  parameterIds,
  keycloakService,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [showNotes, setShowNotes] = useState(false)
  const activeTab = useAppSelector(selectedLmsActiveTab)
  const applicableRange = getApplicableRange(
    test?.lab_test?.test_result_limits ?? [],
    patient
  )

  useEffect(() => {
    if (test?.id === activeId) {
      inputRef.current?.focus()
    }
  }, [activeId, test?.id])

  const handleKeyUp = (ev: KeyboardEvent) => {
    if (ev.key === Enter) {
      onEnter(test?.id)
    }
  }

  const handleSummaryChange = (str: string) => {
    const values = test?.values
    if (values) {
      onValuesChange({ ...values, summary: str }, test?.id)
    } else {
      onValuesChange({ summary: str, observations: [] }, test?.id)
    }
  }

  const handleObservationChange = (obs: LmsObservation[]) => {
    const values = test?.values
    if (values) {
      onValuesChange({ ...values, observations: obs }, test?.id)
    } else {
      onValuesChange({ summary: '', observations: obs }, test?.id)
    }
  }

  const isDisabled = isEdit
    ? false
    : disabled ||
      test?.test_status === orderStatusRejected ||
      (!!test?.referred_out && !isReferred)

  return (
    <div
      className={`test-row ${
        test?.test_status === orderStatusRejected ? REJECTED_TEST : ''
      }`}
    >
      <div className='test-details'>
        <span className='test-name'>{test?.lab_test?.name}</span>
        <div className='limit-details'>
          <span>
            {applicableRange?.min_value || applicableRange?.max_value
              ? `${applicableRange?.min_value ?? 0} to ${
                  applicableRange?.max_value
                }`
              : NO_VALID_RANGE}{' '}
          </span>
          <span>{applicableRange?.test_unit_type?.unit_type ?? 'N/A'}</span>
        </div>
        {test?.test_status === orderStatusRejected && (
          <span className='rejected-info'>
            Test Rejected by {test?.rejected_by_name} due to reason{' '}
            {test?.rejection_reason}{' '}
            <span className='rejected-date'>
              {fullDateTimeFormat(new Date(test?.rejected_on ?? ''))}
            </span>
          </span>
        )}
      </div>
      {test?.lab_test?.test_result_type?.result_type === observationText ? (
        <ObservationList
          disabled={isDisabled && !keycloakService.hasRole([LMS_RESULT_EDIT])}
          values={test?.values}
          onSummaryChange={handleSummaryChange}
          onObservationChange={handleObservationChange}
        />
      ) : (
        <>
          <div className='value-input'>
            {test?.lab_test?.test_result_type?.result_type === NUMERIC && (
              <>
                <input
                  ref={inputRef}
                  title={
                    isOutsideNormalRange(test?.value, applicableRange)
                      ? OUT_SIDE_NORMAL_RANGE
                      : ''
                  }
                  type='number'
                  disabled={
                    (isDisabled ||
                      (activeTab === LmsTestTab &&
                        parameterIds?.includes(test?.lab_test?.id))) &&
                    !keycloakService?.hasRole([LMS_RESULT_EDIT])
                  }
                  value={test?.value ?? ''}
                  onChange={({ target }) =>
                    onValueChange(target?.value, test?.id)
                  }
                  onKeyUp={handleKeyUp}
                  className={
                    isOutsideNormalRange(test?.value, applicableRange)
                      ? ABNORMAL
                      : ''
                  }
                />
                <span>
                  {applicableRange?.test_unit_type?.unit_type ?? 'N/A'}
                </span>
              </>
            )}
            {test?.lab_test?.test_result_type?.result_type ===
              NUMERIC_OUTCOME && (
              <>
                <input
                  ref={inputRef}
                  title={
                    isOutsideNormalRange(test?.value, applicableRange)
                      ? OUT_SIDE_NORMAL_RANGE
                      : ''
                  }
                  type='number'
                  disabled={
                    (isDisabled ||
                      (activeTab === LmsTestTab &&
                        parameterIds?.includes(test?.lab_test?.id))) &&
                    !keycloakService.hasRole([LMS_RESULT_EDIT])
                  }
                  value={test?.value ?? ''}
                  onChange={({ target }) =>
                    onValueChange(target?.value, test?.id)
                  }
                  onKeyUp={handleKeyUp}
                  className={
                    isOutsideNormalRange(test?.value, applicableRange)
                      ? ABNORMAL
                      : ''
                  }
                />
                <span>
                  {applicableRange?.test_unit_type?.unit_type ?? 'N/A'}
                </span>
              </>
            )}
            {test?.lab_test?.test_result_type?.result_type === SHORT_TEXT && (
              <>
                <input
                  ref={inputRef}
                  type='text'
                  disabled={
                    (isDisabled ||
                      (activeTab === LmsTestTab &&
                        parameterIds?.includes(test?.lab_test?.id))) &&
                    !keycloakService.hasRole([LMS_RESULT_EDIT])
                  }
                  value={test?.value ?? ''}
                  onChange={({ target }) =>
                    onValueChange(target?.value, test?.id)
                  }
                  onKeyUp={handleKeyUp}
                />
                <span>
                  {applicableRange?.test_unit_type?.unit_type ?? 'N/A'}
                </span>
              </>
            )}
            {test?.lab_test?.test_result_type?.result_type !== 'Numeric' &&
              test?.lab_test?.test_result_type?.result_type !==
                NUMERIC_OUTCOME &&
              test?.lab_test?.test_result_type?.result_type !== SHORT_TEXT && (
                <>
                  <select
                    name='Value'
                    className={
                      applicableRange?.abnormal &&
                      isAbnormal(applicableRange?.abnormal, test?.value ?? '')
                        ? ABNORMAL
                        : ''
                    }
                    disabled={
                      isDisabled && !keycloakService.hasRole([LMS_RESULT_EDIT])
                    }
                    value={test?.value ?? ''}
                    onChange={({ target }) =>
                      onValueChange(target?.value, test?.id)
                    }
                  >
                    <option value=''>Choose an option</option>
                    {test?.lab_test?.test_result_type?.options?.length > 0 &&
                      test?.lab_test?.test_result_type?.options?.map((tlto) => (
                        <option key={tlto} value={tlto}>
                          {tlto}
                        </option>
                      ))}
                  </select>
                  <input
                    type='text'
                    placeholder='Remarks'
                    className='extra-value'
                    disabled={
                      isDisabled && !keycloakService?.hasRole([LMS_RESULT_EDIT])
                    }
                    value={test?.extra_value ?? ''}
                    onChange={({ target }) =>
                      onExtraValueChange(target?.value, test?.id)
                    }
                  />
                </>
              )}
          </div>
          <div className='notes'>
            {test?.test_status !== orderStatusRejected && (
              <button
                disabled={
                  isDisabled && !keycloakService?.hasRole([LMS_RESULT_EDIT])
                }
                onClick={() => setShowNotes((sh) => !sh)}
              >
                {showNotes ? HIDE_NOTES : SHOW_NOTES}
              </button>
            )}
            {showNotes && (
              <textarea
                className='notes-area'
                name='notes'
                placeholder='Result Notes'
                value={decodeNewLineText(test?.observation)}
                onChange={(ev) => onNotesChange(ev.target?.value, test?.id)}
                cols={45}
                rows={4}
                disabled={isDisabled}
              ></textarea>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default TestRow
